// Net-worth trend series for `#s02` (execution plan §9 conflict #9 / §7.2
// WS-2 step 5): there is NO live endpoint for trend history in M1. This hook
// is written against the shape the backend will owe (a date-ordered series
// of `actual` / `carried` / `gap` points, plus the three headline deltas)
// so wiring it later is a queryFn swap and nothing else. Until that ships it
// resolves to `{ status: 'unavailable' }` -- never a fabricated series --
// and `TrendPage` maps that to the `awaiting-first-snapshot` honest state.
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { RangeOption } from '@/app/shell/AppShell';

export const chartPointSchema = z.object({
  date: z.string(),
  // SIGNED decimal string; null only for a `gap` point, never coerced to '0'.
  value: z.string().nullable(),
  kind: z.enum(['actual', 'carried', 'gap']),
});

export const deltaSchema = z.object({
  value: z.string(), // signed decimal string
  percent: z.string().optional(),
});

export const netWorthSeriesDataSchema = z.object({
  points: z.array(chartPointSchema),
  deltas: z.object({
    today: deltaSchema,
    week: deltaSchema,
    month: deltaSchema,
  }),
  rangeDelta: deltaSchema.optional(),
  // Degradation visibility (§6.3): counts and oldest as-of over non-`ok`
  // contributors to the series, so a partial trend is never shown as clean.
  staleAccountCount: z.number(),
  neverReportedCount: z.number(),
  oldestAsOf: z.string().nullable(),
});

export const netWorthSeriesResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('unavailable') }),
  z.object({ status: z.literal('ok'), data: netWorthSeriesDataSchema }),
]);

export type ChartPoint = z.infer<typeof chartPointSchema>;
export type NetWorthDelta = z.infer<typeof deltaSchema>;
export type NetWorthSeriesData = z.infer<typeof netWorthSeriesDataSchema>;
export type NetWorthSeriesResult = z.infer<typeof netWorthSeriesResultSchema>;

export function useNetWorthSeries(range: RangeOption | 'Custom') {
  return useQuery({
    queryKey: ['networth', 'series', range],
    queryFn: async (): Promise<NetWorthSeriesResult> => {
      return { status: 'unavailable' };
    },
  });
}
